import { useMemo } from 'react';
import { isSameDay, toDateKey, toLocalISO } from '../../../../../utils/dateUtils';

/**
 * Hook to calculate non-overlapping stacking of all-day bars.
 * Includes injection of ghost items during drag interactions.
 */
export function useAllDayStacking(days, allDayByKey, interaction) {
    return useMemo(() => {
        if (days.length === 0) return { layouts: [], totalCols: 1 };
        const viewStart = days[0];
        const viewEnd = days[days.length - 1];
        
        const uniqueAssignments = new Map();
        days.forEach(d => {
            const key = toDateKey(d);
            (allDayByKey[key] || []).forEach(a => {
                uniqueAssignments.set(a.id, a);
            });
        });

        // Inject ghost item during interaction
        if (interaction?.moved) {
            const dragId = interaction.assignment.id;
            const isResize = interaction.type === 'resize-top' || interaction.type === 'resize-bottom';
            
            uniqueAssignments.forEach((val, key) => {
                if (String(key) === String(dragId)) uniqueAssignments.delete(key);
            });

            const showGhostInAllDay = !interaction.isOverSidebar && ((interaction.isOverAllDay && interaction.currentS && interaction.currentE) || isResize);
            if (showGhostInAllDay) {
                let ghostS, ghostE;
                if (isResize) {
                    ghostS = new Date(interaction.currentS);
                    ghostE = new Date(interaction.currentE);
                } else {
                    ghostS = new Date(interaction.currentDay || new Date(interaction.origS));
                    ghostS.setHours(0,0,0,0);
                    const durationMs = new Date(interaction.origE) - new Date(interaction.origS);
                    ghostE = new Date(ghostS.getTime() + durationMs);
                }
                
                const ghost = {
                    ...interaction.assignment,
                    id: 'ghost-' + interaction.assignment.id,
                    isGhost: true,
                    status: (interaction.assignment.status === 'undatiert' || interaction.assignment.status === 'geplant' || !interaction.assignment.status) ? 'aktiv' : interaction.assignment.status,
                    start_datetime: toLocalISO(ghostS),
                    end_datetime: toLocalISO(ghostE)
                };
                uniqueAssignments.set(ghost.id, ghost);
            }
        }
        
        const events = Array.from(uniqueAssignments.values())
            .sort((a, b) => new Date(a.start_datetime || a.created_at) - new Date(b.start_datetime || b.created_at));
            
        const columns = [];
        const layouts = [];

        events.forEach(event => {
            let eStart = new Date(event.start_datetime || event.created_at);
            let eEnd = new Date(event.end_datetime || eStart);
            eStart.setHours(0,0,0,0);
            eEnd.setHours(0,0,0,0);
            
            const renderStart = eStart < viewStart ? viewStart : eStart;
            const renderEnd = eEnd > viewEnd ? viewEnd : eEnd;
            
            const startDayIdx = days.findIndex(d => isSameDay(d, renderStart));
            const endDayIdx = days.findIndex(d => isSameDay(d, renderEnd));
            if (startDayIdx === -1 && endDayIdx === -1) return;
            
            const actualStartIdx = startDayIdx !== -1 ? startDayIdx : 0;
            const actualEndIdx = endDayIdx !== -1 ? endDayIdx : days.length - 1;
            
            let placed = false;
            for (let col = 0; col < columns.length; col++) {
                const colEnd = columns[col];
                if (eStart > colEnd) {
                    columns[col] = eEnd;
                    layouts.push({ event, col, actualStartIdx, actualEndIdx });
                    placed = true;
                    break;
                }
            }
            if (!placed) {
                columns.push(eEnd);
                layouts.push({ event, col: columns.length - 1, actualStartIdx, actualEndIdx });
            }
        });
        
        return { layouts, totalCols: columns.length || 1 };
    }, [allDayByKey, days, interaction]);
}
